import React, { ReactNode, useState, useMemo } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import EmptyState from './EmptyState';
import LoadingSpinner from './LoadingSpinner';

export interface ColumnDef<T> {
  /** Column key (must match data object key) */
  key: keyof T | string;
  /** Column header label */
  label: string;
  /** Custom render function */
  render?: (row: T) => ReactNode;
  /** Column width */
  width?: string;
  /** Enable sorting for this column */
  sortable?: boolean;
  /** Align content */
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T> {
  /** Table columns configuration */
  columns: ColumnDef<T>[];
  /** Table data */
  data: T[];
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Empty state icon */
  emptyIcon?: React.ComponentType;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Actions column */
  actions?: (row: T) => ReactNode;
  /** Enable sorting */
  sortable?: boolean;
  /** Enable pagination */
  pagination?: boolean;
  /** Items per page */
  itemsPerPage?: number;
  /** Row key extractor */
  getRowKey?: (row: T, index: number) => string | number;
  /** Striped rows */
  striped?: boolean;
  /** Hover effect on rows */
  hoverable?: boolean;
}

function DataTable<T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'Nenhum dado encontrado',
  emptyIcon,
  onRowClick,
  actions,
  sortable = true,
  pagination = true,
  itemsPerPage = 10,
  getRowKey,
  striped = false,
  hoverable = true,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<keyof T | string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortKey || !sortable) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortKey as keyof T];
      const bVal = b[sortKey as keyof T];

      if (aVal === bVal) return 0;

      const comparison = aVal > bVal ? 1 : -1;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, sortKey, sortOrder, sortable]);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = pagination
    ? sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : sortedData;

  const handleSort = (key: keyof T | string, isSortable?: boolean) => {
    if (!sortable || isSortable === false) return;

    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (key: keyof T | string, isSortable?: boolean) => {
    if (!sortable || isSortable === false) return null;

    if (sortKey !== key) return <FaSort className="text-[#888]" />;
    return sortOrder === 'asc' ? <FaSortUp className="text-[#089453]" /> : <FaSortDown className="text-[#089453]" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" message="Carregando dados..." />
      </div>
    );
  }

  if (data.length === 0) {
    const Icon = emptyIcon || (() => null);
    return <EmptyState icon={Icon as any} title={emptyMessage} />;
  }

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full">
          <thead className="bg-[#1a2332] border-b border-white/10">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={String(column.key) || index}
                  className={`
                    px-6 py-4 text-sm font-semibold text-[#ccc] 
                    ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                    ${sortable && column.sortable !== false ? 'cursor-pointer hover:text-white select-none' : ''}
                  `}
                  style={{ width: column.width }}
                  onClick={() => handleSort(column.key, column.sortable)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    {getSortIcon(column.key, column.sortable)}
                  </div>
                </th>
              ))}
              {actions && (
                <th className="px-6 py-4 text-sm font-semibold text-[#ccc] text-center">
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => {
              const key = getRowKey ? getRowKey(row, rowIndex) : rowIndex;
              return (
                <tr
                  key={key}
                  className={`
                    border-b border-white/5
                    ${striped && rowIndex % 2 === 0 ? 'bg-[#1a2332]/30' : ''}
                    ${hoverable ? 'hover:bg-[#253547]/50' : ''}
                    ${onRowClick ? 'cursor-pointer' : ''}
                    transition-colors
                  `}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={String(column.key) || colIndex}
                      className={`
                        px-6 py-4 text-sm text-white
                        ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                      `}
                    >
                      {column.render ? column.render(row) : String(row[column.key as keyof T] || '-')}
                    </td>
                  ))}
                  {actions && (
                    <td
                      className="px-6 py-4 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {actions(row)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-[#1a2332] rounded-xl border border-white/10">
          <div className="text-sm text-[#888]">
            Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
            {Math.min(currentPage * itemsPerPage, data.length)} de {data.length} resultados
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 text-[#888] hover:text-white hover:bg-[#253547] rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronLeft />
            </button>

            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`
                    px-3 py-1 rounded-lg font-semibold transition-all
                    ${
                      currentPage === page
                        ? 'bg-gradient-to-r from-[#089453] to-[#84cc34] text-white'
                        : 'text-[#888] hover:text-white hover:bg-[#253547]'
                    }
                  `}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-[#888] hover:text-white hover:bg-[#253547] rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
